import { useQuery } from "@tanstack/react-query";
import { CalendarPlus, Clock3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { appointmentsAPI } from "@/lib/appointments-api";
import { queryKeys } from "@/lib/query-keys";

export type AppointmentPaymentTab = "add-appointment" | "awaiting-payment";

interface AppointmentPaymentTabsProps {
  activeTab: AppointmentPaymentTab;
  onTabChange?: (tab: AppointmentPaymentTab) => void;
}

const page = 1;
const pageSize = 50;

export const AppointmentPaymentTabs = ({
  activeTab,
  onTabChange,
}: AppointmentPaymentTabsProps) => {
  const navigate = useNavigate();
  const awaiting = useQuery({
    queryKey: queryKeys.appointments.awaitingPayments(page, pageSize),
    queryFn: () => appointmentsAPI.getAwaitingPayments(page, pageSize),
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  const changeTab = (value: string) => {
    const tab = value as AppointmentPaymentTab;
    if (onTabChange) {
      onTabChange(tab);
      return;
    }
    navigate(`/settings/appointments/${tab}`);
  };

  return (
    <div className="rounded-2xl border border-border/60 bg-card px-4 shadow-sm">
      <Tabs value={activeTab} onValueChange={changeTab} className="shrink-0">
        <TabsList
          variant="line"
          className="h-12 w-full justify-start gap-5 overflow-x-auto"
        >
          <TabsTrigger
            value="add-appointment"
            className="z-body-md flex-none px-1.5"
          >
            <CalendarPlus />
            Add Appointment
          </TabsTrigger>
          <TabsTrigger
            value="awaiting-payment"
            className="z-body-md flex-none px-1.5"
          >
            <Clock3 />
            Awaiting Payment
            <Badge
              variant={(awaiting.data?.count ?? 0) > 0 ? "warning" : "neutral"}
              className="z-caption z-numeric ml-1 min-w-6 justify-center"
            >
              {awaiting.data?.count ?? 0}
            </Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
};
